import { createRouter, createWebHistory } from 'vue-router'
import { authService } from '@/services/auth'

// Route guards
const requireAuth = (to: any, from: any, next: any) => {
  if (authService.isAuthenticated()) {
    next()
  } else {
    // Store the intended destination for redirect after login
    if (to.path !== '/login') {
      localStorage.setItem('intended_redirect', to.fullPath)
    }
    next('/login')
  }
}

const requireAdmin = (to: any, from: any, next: any) => {
  if (authService.isAuthenticated() && authService.isAdmin()) {
    next()
  } else if (authService.isAuthenticated()) {
    next('/dashboard')
  } else {
    // Store the intended destination for redirect after login
    if (to.path !== '/login') {
      localStorage.setItem('intended_redirect', to.fullPath)
    }
    next('/login')
  }
}

const requireOperator = (to: any, from: any, next: any) => {
  if (authService.isAuthenticated() && (authService.isAdmin() || authService.isOperator())) {
    next()
  } else if (authService.isAuthenticated()) {
    next('/dashboard')
  } else {
    // Store the intended destination for redirect after login
    if (to.path !== '/login') {
      localStorage.setItem('intended_redirect', to.fullPath)
    }
    next('/login')
  }
}

const redirectIfAuthenticated = (to: any, from: any, next: any) => {
  // Check if we're in a post-login redirect state
  const isPostLogin = sessionStorage.getItem('post_login_redirect')

  if (authService.isAuthenticated() && !isPostLogin) {
    // Clear any stored redirect since user is already authenticated
    localStorage.removeItem('intended_redirect')

    // Check if there's a redirect query parameter
    const redirect = to.query.redirect as string
    if (redirect && redirect !== '/login') {
      next(redirect)
    } else {
      next('/dashboard')
    }
  } else {
    // Clear the post-login flag if it exists
    if (isPostLogin) {
      sessionStorage.removeItem('post_login_redirect')
    }
    next()
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      redirect: '/dashboard',
    },
    {
      path: '/login',
      name: 'Login',
      component: () => import('@/views/auth/LoginView.vue'),
      beforeEnter: redirectIfAuthenticated,
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: () => import('@/views/EnhancedDashboardView.vue'),
      beforeEnter: requireAuth,
    },
    {
      path: '/vehicles',
      name: 'Vehicles',
      component: () => import('@/views/vehicles/VehicleListView.vue'),
      beforeEnter: requireOperator,
    },
    {
      path: '/vehicles/create',
      name: 'CreateVehicle',
      component: () => import('@/views/vehicles/VehicleCreateView.vue'),
      beforeEnter: requireOperator,
    },
    {
      path: '/vehicles/:id',
      name: 'VehicleDetails',
      component: () => import('@/views/vehicles/VehicleDetailsView.vue'),
      beforeEnter: requireOperator,
    },
    {
      path: '/vehicles/:id/edit',
      name: 'EditVehicle',
      component: () => import('@/views/vehicles/VehicleEditView.vue'),
      beforeEnter: requireOperator,
    },
    {
      path: '/routes',
      name: 'Routes',
      component: () => import('@/views/routes/RouteListView.vue'),
      beforeEnter: requireOperator,
    },
    {
      path: '/routes/create',
      name: 'CreateRoute',
      component: () => import('@/views/routes/RouteCreateView.vue'),
      beforeEnter: requireOperator,
    },
    {
      path: '/routes/:id',
      name: 'RouteDetails',
      component: () => import('@/views/routes/RouteDetailsView.vue'),
      beforeEnter: requireOperator,
    },
    {
      path: '/routes/:id/edit',
      name: 'EditRoute',
      component: () => import('@/views/routes/RouteEditView.vue'),
      beforeEnter: requireOperator,
    },
    {
      path: '/admin',
      children: [
        {
          path: 'companies',
          name: 'AdminCompanies',
          component: () => import('@/views/admin/CompanyListView.vue'),
          beforeEnter: requireAdmin,
        },
        {
          path: 'companies/create',
          name: 'CreateCompany',
          component: () => import('@/views/admin/CompanyCreateView.vue'),
          beforeEnter: requireAdmin,
        },
        {
          path: 'companies/:id',
          name: 'CompanyDetails',
          component: () => import('@/views/admin/CompanyDetailsView.vue'),
          beforeEnter: requireAdmin,
        },
        {
          path: 'companies/:id/edit',
          name: 'EditCompany',
          component: () => import('@/views/admin/CompanyEditView.vue'),
          beforeEnter: requireAdmin,
        },
        {
          path: 'users',
          name: 'AdminUsers',
          component: () => import('@/views/admin/UserListView.vue'),
          beforeEnter: requireAdmin,
        },
      ],
    },
    {
      path: '/schedules',
      name: 'Schedules',
      component: () => import('@/views/schedules/ScheduleListView.vue'),
      beforeEnter: requireOperator,
    },
    {
      path: '/schedules/create',
      name: 'CreateSchedule',
      component: () => import('@/views/schedules/ScheduleCreateView.vue'),
      beforeEnter: requireOperator,
    },
    {
      path: '/schedules/:id',
      name: 'ScheduleDetails',
      component: () => import('@/views/schedules/ScheduleDetailsView.vue'),
      beforeEnter: requireOperator,
    },
    {
      path: '/schedules/:id/edit',
      name: 'EditSchedule',
      component: () => import('@/views/schedules/ScheduleEditView.vue'),
      beforeEnter: requireOperator,
    },
    {
      path: '/conductors',
      name: 'Conductors',
      component: () => import('@/views/conductors/ConductorListView.vue'),
      beforeEnter: requireOperator,
    },
    {
      path: '/analytics',
      name: 'Analytics',
      component: () => import('@/views/analytics/AnalyticsView.vue'),
      beforeEnter: requireOperator,
    },
    {
      path: '/profile',
      name: 'Profile',
      component: () => import('@/views/ProfileView.vue'),
      beforeEnter: requireAuth,
    },
    {
      path: '/components',
      name: 'ComponentShowcase',
      component: () => import('@/views/ComponentShowcaseView.vue'),
      beforeEnter: requireAuth,
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      component: () => import('@/views/NotFoundView.vue'),
    },
  ],
})

export default router
